import { type RefObject, useState } from 'react';
import { TextInput, type TextInputProps, View } from 'react-native';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';

type SearchBarProps = TextInputProps & {
  threshold?: number;
  onPressCancel?: () => void;
};

const SearchBar = withUnistyles(SearchBarComponent, (theme) => ({
  placeholderTextColor: theme.colors.typographySecondary,
}));

function SearchBarComponent({
  ref,
  children,
  ...props
}: SearchBarProps & { ref?: RefObject<TextInput> }) {
  const [isFocused, setIsFocused] = useState(false);
  const onFocus = () => setIsFocused(true);
  const onBlur = () => setIsFocused(false);

  const { onChangeText, value, ...restProps } = props;

  return (
    <View style={style.inputContainer(isFocused)}>
      {children}

      <TextInput
        ref={ref}
        placeholder="Search..."
        autoCapitalize="none"
        value={value}
        onChangeText={onChangeText}
        enterKeyHint="done"
        style={style.textInput}
        onFocus={onFocus}
        onBlur={onBlur}
        {...restProps}
      />
    </View>
  );
}

const style = StyleSheet.create((theme) => ({
  children: {
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  inputContainer: (isFocused: boolean) => ({
    borderWidth: theme.size(0.5),
    borderColor: isFocused ? theme.colors.primary : theme.colors.borderMuted,
    borderRadius: theme.borderRadius.lg,
    paddingHorizontal: theme.size(2),
    paddingVertical: theme.size(2),
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.size(1),
    minHeight: theme.size(10),
  }),
  textInput: {
    flex: 1,
    color: theme.colors.typography,
    fontSize: theme.fontSize.md,
    minWidth: theme.size(20),
    paddingVertical: 0,
    _web: {
      outlineColor: 'transparent',
      outlineWidth: 0,
      outlineStyle: 'none',
      _focus: {
        outlineColor: 'transparent',
        outlineWidth: 0,
        outlineStyle: 'none',
      },
    },
  },
}));

export default SearchBar;
